import { useEffect, useMemo, useRef, useState } from "react";
import { BoolFilter } from "../../components/inputs/BoolFilter";
import { EnumFilter } from "../../components/inputs/EnumFilter";
import { Toolbar } from "../../components/Toolbar";
import { api } from "../../util/api";
import { deepCompact, snakifyKeys } from "../../util/helpers";
import { encodeFilterParams } from "../../util/table";
import { AdministrateBackgroundExportButton } from "../components/AdministrateBackgroundExportButton";
import { AdministrateTable } from "../components/AdministrateTable";
import { DESTROY, UPDATE, useCreatePermission } from "../utils";
import { AutoDeliveryEnabledCell } from "./components/AutoDeliveryEnabledCell";
import { ImportReportDeliveriesModal } from "./components/ImportReportDeliveriesModal";
import { AddReportDeliveryModal } from "./components/AddReportDeliveryModal";
import { ReportDeliveryActionCell } from "./components/ReportDeliveryActionCell";
import { DetailedErrorAlert } from "../../components/DetailedErrorAlert";
import { RunSeeqerCaseResultsButton } from "../components/RunSeeqerCaseResultsButton";
import { isNil } from "lodash-es";

const useColumns = (reportTypeOptions, deliveryMethodOptions, resourceId, rawResourceId, resourceType) =>
  useMemo(
    () => [
      {
        id: "delivery_method",
        accessor: "deliveryMethod",
        Header: "Delivery Method",
        Filter: (props) => <EnumFilter {...props} options={deliveryMethodOptions} />,
      },
      {
        id: "report_type",
        accessor: "reportType",
        Header: "Report Type",
        Filter: (props) => <EnumFilter {...props} options={reportTypeOptions} />,
        disableFilters: resourceType == "clinic",
        disableSortBy: resourceType == "clinic",
      },
      {
        id: "target",
        accessor: "target",
        Header: "Target",
      },
      {
        id: "auto_delivery_enabled",
        accessor: "autoDeliveryEnabled",
        Header: "Auto Delivery Enabled",
        Cell: AutoDeliveryEnabledCell,
        Filter: (props) => <BoolFilter {...props} yesLabel="Enabled" noLabel="Not Enabled" />,
      },
      {
        id: "actions",
        Header: "Actions",
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ row }) => (
          <ReportDeliveryActionCell resourceId={resourceId} rawResourceId={rawResourceId} resourceType={resourceType} {...row} />
        ),
      },
    ],
    [reportTypeOptions, deliveryMethodOptions, resourceId, rawResourceId, resourceType]
  );

const getReportTypeOptions = async (setReportTypeOptions, resourceType) => {
  try {
    const { data } = await api.get(
      `/admin/report_delivery_configs/report_type_options.json?report_delivery_config[resource_type]=${resourceType}`
    );
    setReportTypeOptions(data.resources);
  } catch (err) {
    console.error(err);
  }
};

const getDeliveryMethodOptions = async (setDeliveryMethodOptions, resourceType) => {
  try {
    const { data } = await api.get(
      `/admin/report_delivery_configs/delivery_method_options.json?report_delivery_config[resource_type]=${resourceType}`
    );
    setDeliveryMethodOptions(data.resources);
  } catch (err) {
    console.error(err);
  }
};

export const ReportDeliveriesTab = ({ resourceType, lab, clinic }) => {
  const tableRef = useRef();
  const [reportTypeOptions, setReportTypeOptions] = useState([]);
  const [deliveryMethodOptions, setDeliveryMethodOptions] = useState([]);

  const resourceId = resourceType == "lab" ? lab.labSetting?.id : clinic.id;
  const rawResourceId = resourceType == "lab" ? lab.id : clinic.id;
  const resourceKey = resourceType == "lab" ? "labSettingId" : "clinicId";

  const defaultColumns = useColumns(reportTypeOptions, deliveryMethodOptions, resourceId, rawResourceId, resourceType);
  const filteredColumns = defaultColumns.filter((col) => col.id != "target");

  const [tableFilters, setTableFilters] = useState({});
  const [canCreate] = useCreatePermission("ReportDeliveryConfig");

  const extraParams = useMemo(() => deepCompact({ [resourceKey]: resourceId }), [resourceId, resourceKey]);

  useEffect(() => {
    getReportTypeOptions(setReportTypeOptions, resourceType);
    getDeliveryMethodOptions(setDeliveryMethodOptions, resourceType);
  }, [resourceType]);

  const allFilters = useMemo(
    () => ({
      ...snakifyKeys(extraParams),
      ...encodeFilterParams(tableFilters),
    }),
    [extraParams, tableFilters]
  );

  const onImport = (message) => {
    const { refresh, setMessage } = tableRef.current;
    setMessage(message);
    refresh();
  };

  const onAddReportDelivery = (message) => {
    const { refresh, setMessage } = tableRef.current;

    setMessage(`Successfully Added ${message}`);
    refresh();
  };

  if (isNil(resourceId)) {
    return <DetailedErrorAlert error={"No resource."} />;
  }

  return (
    <>
      <Toolbar className="align-items-center mb-3">
        <Toolbar className="ms-auto">
          {resourceType == "clinic" && <RunSeeqerCaseResultsButton outline className="me-2" resourceId={resourceId} ref={tableRef} />}

          <AdministrateBackgroundExportButton
            outline
            className="me-2"
            exportUrl={`/admin/report_delivery_configs/export?report_delivery_config[resource_type]=${resourceType}`}
            filters={allFilters}
            ref={tableRef}
          />

          {canCreate && (
            <>
              <ImportReportDeliveriesModal
                outline
                className="me-2"
                onImport={onImport}
                resourceId={resourceId}
                resourceType={resourceType}
              />
              <AddReportDeliveryModal
                color="primary"
                onAdd={onAddReportDelivery}
                resourceId={resourceId}
                rawResourceId={rawResourceId}
                resourceType={resourceType}
              />
            </>
          )}
        </Toolbar>
      </Toolbar>

      <AdministrateTable
        resourceUrl={"/admin/report_delivery_configs.json"}
        extraParams={extraParams}
        permissions={[UPDATE, DESTROY]}
        resourceName={"report_delivery_config"}
        onFilteredChange={setTableFilters}
        columns={resourceType == "lab" ? defaultColumns : filteredColumns}
        ref={tableRef}
      />
    </>
  );
};
